import classNames from "classnames";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { flattenChildren, getReactNodeKey } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	PropsWithChildren,
	ReactElement,
	ReactNode,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { Button } from "../Button/Button";
import { GridItem } from "../GridItem/GridItem";
import styles from "./AttachmentList.module.less";

export type AttachmentListProps = {
	trackingId?: string;
};

export const AttachmentList = (props: PropsWithChildren<AttachmentListProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	const listRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const [intersectionObserver, setIntersectionObserver] = useState<IntersectionObserver | null>(null);
	const [visibleElements, setVisibleElements] = useState<ReadonlySet<Element>>(new Set());
	const [shouldShowAllAttachments, setShouldShowAllAttachments] = useState<boolean>(false);

	const childrenArray: ReactNode[] = flattenChildren(children);

	useEffect(
		(): EffectWithDestructorResult => {
			if (shouldShowAllAttachments) {
				return undefined;
			}

			if (!listRef.current) {
				return undefined;
			}

			const intersectionObserver = new IntersectionObserver(
				(entries: IntersectionObserverEntry[]): void => {
					setVisibleElements(
						visibleElements => {
							for (const entry of entries) {
								visibleElements = toggleItemVisibility(
									visibleElements,
									entry.target,
									entry.intersectionRatio === 1
								);
							}

							return visibleElements;
						}
					);
				},
				{
					root: listRef.current,
					threshold: 1
				}
			);

			setIntersectionObserver(intersectionObserver);

			return (): void => {
				intersectionObserver.disconnect();
				setIntersectionObserver(null);
			};
		},
		[shouldShowAllAttachments]
	);

	const observeElementHandler = useCallback(
		(element: Element): (() => void) | undefined => {
			if (!intersectionObserver) {
				return undefined;
			}

			intersectionObserver.observe(element);

			return (): void => {
				intersectionObserver.unobserve(element);
				setVisibleElements(
					visibleElements => {
						const copy = new Set(visibleElements);

						copy.delete(element);
						return copy;
					}
				);
			};
		},
		[intersectionObserver]
	);

	const getIsVisibleHandler = useCallback(
		(element: Element): boolean => {
			return shouldShowAllAttachments
				|| visibleElements.has(element);
		},
		[visibleElements, shouldShowAllAttachments]
	);

	const hasMoreItems: boolean = !shouldShowAllAttachments
		&& childrenArray.length > visibleElements.size;

	return (
		<div className={styles.root} {...setTrackingId(trackingId)}>
			<ul
				className={classNames(
					styles.list,
					{[styles.list_showAll]: shouldShowAllAttachments}
				)}
				ref={listRef}
			>
				{childrenArray.map(
					(child, index) => (
						<ItemWrapper
							onObserveVisibility={observeElementHandler}
							getIsVisible={getIsVisibleHandler}
							key={getReactNodeKey(child, index)}
						>
							{child}
						</ItemWrapper>
					)
				)}
			</ul>
			{(hasMoreItems || shouldShowAllAttachments) && (
				<div className={styles.attachmentButtons}>
					{hasMoreItems && (
						<Button
							kind="transparent"
							text={localize("_ShowAllAttachments")}
							onClick={() => setShouldShowAllAttachments(true)}
							icon="chevron-down"
							trackingId="show-all-button"
						/>
					)}
					{shouldShowAllAttachments && (
						<Button
							kind="transparent"
							text={localize("_ShowLessAttachments")}
							onClick={() => setShouldShowAllAttachments(false)}
							icon="chevron-up"
							trackingId="show-less-button"
						/>
					)}
				</div>
			)}
		</div>
	);
};
AttachmentList.displayName = "AttachmentList";

AttachmentList.Item = GridItem;

type ItemWrapperProps = {
	getIsVisible(target: HTMLElement): boolean;
	onObserveVisibility(target: HTMLElement): (() => void) | undefined;
};

const ItemWrapper = (props: PropsWithChildren<ItemWrapperProps>): ReactElement => {
	const {
		getIsVisible,
		onObserveVisibility,
		children
	} = props;

	const itemRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);

	useEffect(
		(): EffectWithDestructorResult => {
			if (!itemRef.current) {
				return undefined;
			}

			const element: HTMLLIElement = itemRef.current;
			return onObserveVisibility(element);
		},
		[
			onObserveVisibility
		]
	);

	return (
		<li
			ref={itemRef}
			className={classNames(
				styles.item,
				{[styles.item_hidden]: isNotNil(itemRef.current) && !getIsVisible(itemRef.current)}
			)}
		>
			{children}
		</li>
	);

};
ItemWrapper.displayName = "ItemWrapper";

function toggleItemVisibility(
	visibleElements: ReadonlySet<Element>,
	item: Element,
	shouldBeVisible: boolean
): ReadonlySet<Element> {
	const isCurrentlyVisible: boolean = visibleElements.has(item);

	if (isCurrentlyVisible === shouldBeVisible) {
		return visibleElements;
	}

	const copy: Set<Element> = new Set(visibleElements);
	if (shouldBeVisible) {
		copy.add(item);
	} else {
		copy.delete(item);
	}

	return copy;
}